import { useClickOutside } from '@mm/hooks';
import { ArrowStatefulIcon, CloseCircleIcon } from '@mm/icons';
import { colors } from '@mm/utils';
import classNames from 'classnames';
import React, {
  Dispatch,
  MouseEvent,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Waypoint } from 'react-waypoint';
import { ThreeDotMenu, ThreeDotMenuProps } from '..';
import { Checkbox } from '../Checkbox/Checkbox';
import { ScrollArea } from '../ScrollArea/ScrollArea';
import { SearchInput } from '../SearchInput/SearchInput';
import { Spinner } from '../Spinner/Spinner';
import { TextLink } from '../TextLink/TextLink';
import './Select.pcss';
import { createPortal } from 'react-dom';

export type Option<T = string | number> = {
  id: T;
  label: JSX.Element | Element | string;
  toString?: () => string;
  disabled?: boolean;
  tooltip?: ReactNode;
} & Record<string, any>;

type Props<T extends Option> = {
  className?: string;
  label?: string;
  placeholder?: string;
  selected?: T[];
  options?: T[];
  isLoading?: boolean;
  disabled?: boolean;
  onSearch?: (query?: string) => void;
  onChange?: (v: T[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onScrollEnd?: () => void;
  withThreeDotMenu?: boolean;
  menuProps?: ThreeDotMenuProps;
  setAudienceId?: Dispatch<SetStateAction<number>>;
  hideClearButton?: boolean; // Hide the clear button which empties the current selection
  hideCount?: boolean; // Hide the counter showing the number of items selected
  singleSelect?: boolean; // Only allow a single selection
  disableClickOutsideExceptions?: boolean; // Don't leave this dropdown open if another one is opened
  staticDropdownPositioning?: boolean; // Freezes the location of the dropdown at the time it is opened
  useNbspInLabels?: boolean;
};
const remapSpaceCharacters = label => label.replaceAll(' ', '\xa0');

export const Select = <T extends Option>({
  options = [],
  selected = [],
  className,
  label,
  placeholder,
  disabled,
  onSearch,
  onChange = () => {},
  onOpen = () => {},
  onClose = () => {},
  onScrollEnd = () => {},
  isLoading,
  withThreeDotMenu,
  menuProps,
  setAudienceId,
  hideClearButton,
  hideCount,
  singleSelect,
  disableClickOutsideExceptions,
  staticDropdownPositioning,
  useNbspInLabels,
}: Props<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const open = () => {
    if (!disabled) {
      setIsOpen(true);
      onOpen();
    }
  };

  const close = () => {
    if (!disabled) {
      setIsOpen(false);
      setSearch('');
      onClose();
    }
  };

  const onSelectClick = () => {
    if (isOpen) {
      return close();
    }

    return open();
  };

  const onSearchChange = e => {
    setSearch(e.target.value);
    onSearch?.(e.target.value);
  };

  const ref = useClickOutside<HTMLDivElement>((e: Event) => {
    if (!disableClickOutsideExceptions) {
      const exception = e
        .composedPath()
        .some((e: Element) => e.className === 'c-select__select');

      if (exception) {
        return;
      }
    }

    close();
  });

  const handleSelect = (opt: Option) => {
    if (opt.disabled) {
      return;
    }

    if (setAudienceId) {
      setAudienceId(Number(opt.id));
    }
    const toAdd = options.find(v => v.id === opt.id);
    if (singleSelect && toAdd) {
      onChange([toAdd]);
      close();
      return;
    }
    const toRemove = selected.some(v => v.id === opt.id);

    const without = selected.filter(v => v.id !== opt.id);

    if (toRemove) {
      return onChange(without);
    }

    if (toAdd) {
      onChange([...without, toAdd]);
    }
  };

  const handleRemove = (e: MouseEvent<HTMLDivElement>) => {
    if (!hideClearButton && !disabled && selected.length) {
      onChange([]);
      e.stopPropagation();
    }
  };
  const [dropdownStyle, setDropdownStyle] = useState({});
  useEffect(() => {
    if (staticDropdownPositioning && isOpen) {
      const {
        offsetTop = 0,
        offsetHeight = 0,
        offsetLeft = 0,
      } = ref.current?.parentElement || {};
      setDropdownStyle({
        top: offsetTop + offsetHeight,
        left: offsetLeft,
      });
    } else {
      setDropdownStyle({});
    }
  }, [isOpen, ref, staticDropdownPositioning]);

  const [tooltip, setTooltip] = useState<ReactNode | undefined>();

  useEffect(() => {
    const ev = () => {
      setTooltip(undefined);
    };

    document.addEventListener('click', ev);

    return document.removeEventListener('click', ev);
  }, []);

  return (
    <div
      className={classNames(className, 'c-select__container', {
        'c-select--focused': isOpen,
        'c-select--disabled': disabled,
        'c-select--staticDropdownPositioning': staticDropdownPositioning,
      })}
    >
      <div className="c-select__select" onClick={onSelectClick}>
        {label && <span className="c-select__label">{label}</span>}

        {!selected.length && placeholder && (
          <span className="c-select__placeholder">{placeholder}</span>
        )}

        {!!selected.length && (
          <span className="c-select__value">
            {selected
              .map(opt =>
                typeof opt.label === 'string' ? opt.label : opt.toString(),
              )
              .map(useNbspInLabels ? remapSpaceCharacters : v => v)
              .join(', ')}
          </span>
        )}

        {!hideCount && !!selected.length && (
          <span className="c-select__count">{selected.length}</span>
        )}

        <div className="c-select__icon" onClick={handleRemove}>
          {!hideClearButton && selected?.length ? (
            <CloseCircleIcon />
          ) : (
            <ArrowStatefulIcon
              color={isOpen ? colors.accentBlue : colors.darkGray}
              size={13}
              direction={isOpen ? 'up' : 'down'}
            />
          )}
        </div>
      </div>

      {isOpen && (
        <div className="c-select__dropdown" ref={ref} style={dropdownStyle}>
          {onSearch && (
            <span className="c-select__search">
              <SearchInput
                placeholder="Search: "
                value={search}
                onChange={onSearchChange}
              />
            </span>
          )}

          {tooltip && (
            <span className="c-select__option-tooltip">
              <span className="c-select__option-tooltip-content">
                {tooltip}
              </span>
            </span>
          )}

          <ScrollArea className="c-select__list">
            {!(search && isLoading) &&
              options.map(option => {
                const isSelected = selected?.some(({ id }) => id === option.id);
                return (
                  <div
                    key={option.id}
                    onClick={() => handleSelect(option)}
                    className={classNames('c-select__item', {
                      'c-select__selected': isSelected,
                    })}
                    onMouseEnter={() => {
                      setTooltip(option.tooltip);
                    }}
                  >
                    <span className="c-select__option">
                      <Checkbox
                        className={classNames('c-select__checkbox', {
                          'c-select__hidden-checkbox': singleSelect,
                        })}
                        isChecked={isSelected}
                        disabled={option.disabled}
                      />
                      <TextLink className="c-select__text">
                        {useNbspInLabels
                          ? remapSpaceCharacters(option.label)
                          : option.label}
                      </TextLink>
                    </span>
                    {withThreeDotMenu && menuProps && (
                      <ThreeDotMenu
                        className={menuProps.className}
                        elements={menuProps.elements}
                        selectItem={menuProps.selectItem}
                        contentClassName={menuProps.contentClassName}
                      />
                    )}
                  </div>
                );
              })}
            {!isLoading && onScrollEnd && (
              <Waypoint bottomOffset="-20px" onEnter={onScrollEnd} />
            )}
            {!selected?.length && isLoading && (
              <span className="c-select__loader">
                <Spinner
                  show
                  size={14}
                  position="relative"
                  color={colors.accentBlue}
                  backgroundColor="transparent"
                />
              </span>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
};
